/**
 * Task #541 — Coach Portal page (/portal).
 *
 * Renders 7 sections: Welcome, Contract overview, Key dates, Compensation,
 * Documents, Tasks/Action items, Recent updates, Contact. Every API call
 * goes through `requireCoach` on the server, so a non-coach signed-in
 * user will see a friendly "no portal access" empty state instead of a
 * console error.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, FileText, Download, CheckCircle, Calendar, DollarSign, Bell, Phone, ShieldOff } from "lucide-react";

type CoachMe = {
  coach: {
    id: number; firstName: string; lastName: string; name: string;
    sport: string; title: string; email: string; phone: string | null;
    hireDate: string | null; status: string; portalStatus: string | null;
    lastPortalLoginAt: string | null;
  };
  contract: {
    id: number; contractType: string; startDate: string | null;
    endDate: string | null; status: string; updatedAt: string;
  } | null;
};

type KeyDate = { label: string; date: string; type?: string; visible: boolean };
type Compensation =
  | { visible: false }
  | {
      visible: true;
      annualSalary: number | null; signingBonus: number | null;
      bonuses: Array<{ id?: string; label: string; amount?: number; eligibilityDate?: string; status?: string }>;
    };
type Doc = {
  id: number; contractId: number; label: string; fileName: string; mimeType: string;
  sizeBytes: number; uploadedAt: string; coachCanDownload: boolean; downloadUrl: string | null;
};
type Task = {
  id: number; title: string; instructions: string | null;
  dueDate: string | null; status: string; taskType: string;
  coachResponse: string | null; hasUpload: boolean; completedAt: string | null;
};
type Update = { kind: string; label: string; at: string };
type Contact = {
  institutionName: string | null; hrContact: string | null;
  sportAdminContact: string | null; supportEmail: string | null;
};

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}
function formatMoney(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function CoachPortal() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"unauthorized" | "no_access" | "error" | null>(null);
  const [me, setMe] = useState<CoachMe | null>(null);
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);
  const [comp, setComp] = useState<Compensation | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [contact, setContact] = useState<Contact | null>(null);

  const apiGet = useCallback(async (path: string) => {
    const token = await getToken();
    return fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  }, [getToken]);

  const apiPost = useCallback(async (path: string, body?: unknown) => {
    const token = await getToken();
    return fetch(path, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  }, [getToken]);

  const reload = useCallback(async () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      // Don't leave the user staring at an indefinite spinner — render the
      // unauthorized empty state, which links to /sign-in.
      setError("unauthorized");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const meRes = await apiGet("/api/portal/me");
      if (meRes.status === 401) { setError("unauthorized"); setLoading(false); return; }
      if (meRes.status === 403) { setError("no_access"); setLoading(false); return; }
      if (!meRes.ok) { setError("error"); setLoading(false); return; }
      setMe(await meRes.json());

      const [kd, c, d, t, u, ct] = await Promise.all([
        apiGet("/api/portal/key-dates").then((r) => r.json()).catch(() => ({ keyDates: [] })),
        apiGet("/api/portal/compensation").then((r) => r.json()).catch(() => ({ visible: false })),
        apiGet("/api/portal/documents").then((r) => r.json()).catch(() => []),
        apiGet("/api/portal/tasks").then((r) => r.json()).catch(() => []),
        apiGet("/api/portal/updates").then((r) => r.json()).catch(() => ({ updates: [] })),
        apiGet("/api/portal/contact").then((r) => r.json()).catch(() => null),
      ]);
      setKeyDates(kd?.keyDates ?? []);
      setComp(c);
      setDocs(Array.isArray(d) ? d : []);
      setTasks(Array.isArray(t) ? t : []);
      setUpdates(u?.updates ?? []);
      setContact(ct);
    } catch {
      setError("error");
    } finally {
      setLoading(false);
    }
  }, [apiGet, isLoaded, isSignedIn]);

  useEffect(() => { void reload(); }, [reload]);

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error === "unauthorized") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardHeader><CardTitle>Sign in required</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">Please sign in with the email your athletic department invited you under.</p>
            <Button className="mt-4" onClick={() => { window.location.href = "/sign-in"; }}>Sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === "no_access" || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-amber-600" /> Portal not yet enabled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Your account is not yet connected to a coach profile in this organization.
              Please contact your administrator to enable portal access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onCompleteTask = async (taskId: number, response?: string) => {
    await apiPost(`/api/portal/tasks/${taskId}/complete`, { coachResponse: response ?? null });
    await reload();
  };

  const onUploadTask = async (taskId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const token = await getToken();
    await fetch(`/api/portal/tasks/${taskId}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    await reload();
  };

  const onAcknowledge = async (docId: number) => {
    await apiPost(`/api/portal/documents/${docId}/acknowledge`);
    await reload();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* 1. Welcome */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-semibold">Welcome, {me.coach.firstName}</h1>
                <p className="text-sm text-slate-600 mt-1">{me.coach.title} · {me.coach.sport}</p>
              </div>
              <Badge variant="outline">Coach Portal</Badge>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="tasks">Action items{tasks.filter(t => t.status !== "completed").length > 0 ? ` (${tasks.filter(t => t.status !== "completed").length})` : ""}</TabsTrigger>
            <TabsTrigger value="updates">Recent updates</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* 2. Contract overview */}
            <Card>
              <CardHeader><CardTitle className="text-base">Your active contract</CardTitle></CardHeader>
              <CardContent>
                {me.contract ? (
                  <dl className="grid grid-cols-2 gap-y-2 text-sm">
                    <dt className="text-slate-500">Type</dt><dd className="font-medium">{me.contract.contractType}</dd>
                    <dt className="text-slate-500">Start date</dt><dd>{formatDate(me.contract.startDate)}</dd>
                    <dt className="text-slate-500">End date</dt><dd>{formatDate(me.contract.endDate)}</dd>
                    <dt className="text-slate-500">Status</dt><dd><Badge variant="default">{me.contract.status}</Badge></dd>
                  </dl>
                ) : (
                  <p className="text-sm text-slate-500">No active contract on file. Contact your administrator if this is unexpected.</p>
                )}
              </CardContent>
            </Card>

            {/* 3. Key dates */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Key dates</CardTitle></CardHeader>
              <CardContent>
                {keyDates.length === 0 ? (
                  <p className="text-sm text-slate-500">No key dates have been published yet.</p>
                ) : (
                  <ul className="divide-y">
                    {keyDates.map((d, i) => (
                      <li key={i} className="py-2 flex justify-between text-sm">
                        <span>{d.label}</span>
                        <span className="font-medium">{formatDate(d.date)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* 4. Compensation */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> Compensation</CardTitle></CardHeader>
              <CardContent>
                {!comp || !comp.visible ? (
                  <p className="text-sm text-slate-500">Compensation details are not currently shared with the portal.</p>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-slate-500">Annual salary</div>
                      <div className="font-medium">{formatMoney(comp.annualSalary)}</div>
                      <div className="text-slate-500">Signing bonus</div>
                      <div className="font-medium">{formatMoney(comp.signingBonus)}</div>
                    </div>
                    {comp.bonuses && comp.bonuses.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mt-2">Performance bonuses</h3>
                        <ul className="divide-y mt-1">
                          {comp.bonuses.map((b, i) => (
                            <li key={i} className="py-2 flex justify-between">
                              <span>{b.label}{b.eligibilityDate ? ` · ${formatDate(b.eligibilityDate)}` : ""}</span>
                              <span className="font-medium">{formatMoney(b.amount ?? null)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 5. Documents */}
          <TabsContent value="documents">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Shared documents</CardTitle></CardHeader>
              <CardContent>
                {docs.length === 0 ? (
                  <p className="text-sm text-slate-500">No documents have been shared with you yet.</p>
                ) : (
                  <ul className="divide-y">
                    {docs.map((d) => (
                      <li key={d.id} className="py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{d.fileName}</p>
                          <p className="text-xs text-slate-500">
                            {d.label} · uploaded {formatDate(d.uploadedAt)}
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => onAcknowledge(d.id)}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Acknowledge
                          </Button>
                          {d.coachCanDownload && d.downloadUrl ? (
                            <a href={d.downloadUrl}>
                              <Button size="sm"><Download className="h-4 w-4 mr-1" /> Download</Button>
                            </a>
                          ) : (
                            <Button size="sm" variant="ghost" disabled>View only</Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 6. Tasks / Action items */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Bell className="h-4 w-4" /> Action items</CardTitle></CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <p className="text-sm text-slate-500">No outstanding tasks. You're all caught up.</p>
                ) : (
                  <ul className="space-y-4">
                    {tasks.map((t) => <TaskRow key={t.id} task={t} onComplete={onCompleteTask} onUpload={onUploadTask} />)}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 7. Recent updates */}
          <TabsContent value="updates">
            <Card>
              <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
              <CardContent>
                {updates.length === 0 ? (
                  <p className="text-sm text-slate-500">No recent activity.</p>
                ) : (
                  <ul className="divide-y">
                    {updates.map((u, i) => (
                      <li key={i} className="py-2 flex justify-between text-sm">
                        <span>{u.label}</span>
                        <span className="text-xs text-slate-500">{new Date(u.at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact */}
          <TabsContent value="contact">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4" /> Contact your athletic department</CardTitle></CardHeader>
              <CardContent>
                {!contact ? (
                  <p className="text-sm text-slate-500">Contact information is not yet configured.</p>
                ) : (
                  <dl className="grid grid-cols-1 gap-y-3 text-sm">
                    {contact.institutionName && (<><dt className="text-slate-500">Institution</dt><dd className="font-medium">{contact.institutionName}</dd></>)}
                    {contact.hrContact && (<><dt className="text-slate-500">HR contact</dt><dd>{contact.hrContact}</dd></>)}
                    {contact.sportAdminContact && (<><dt className="text-slate-500">Sport administrator</dt><dd>{contact.sportAdminContact}</dd></>)}
                    {contact.supportEmail && (<><dt className="text-slate-500">Support email</dt><dd><a className="text-indigo-600 underline" href={`mailto:${contact.supportEmail}`}>{contact.supportEmail}</a></dd></>)}
                  </dl>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Alert>
          <AlertTitle>Privacy reminder</AlertTitle>
          <AlertDescription className="text-xs">
            Your portal activity (logins, downloads, acknowledgments) is logged for compliance. Only authorized administrators in your organization can see it.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

function TaskRow({ task, onComplete, onUpload }: {
  task: Task;
  onComplete: (id: number, response?: string) => Promise<void>;
  onUpload: (id: number, file: File) => Promise<void>;
}) {
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const isDone = task.status === "completed";

  return (
    <li className="border rounded-md p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{task.title}</p>
          {task.dueDate && <p className="text-xs text-slate-500">Due {formatDate(task.dueDate)}</p>}
        </div>
        <Badge variant={isDone ? "secondary" : "default"}>
          {isDone ? "Completed" : task.status}
        </Badge>
      </div>
      {task.instructions && <p className="text-sm text-slate-700 mt-2">{task.instructions}</p>}
      {!isDone && (
        <div className="mt-3 space-y-2">
          {task.taskType === "disclosure" && (
            <div>
              <Label htmlFor={`r-${task.id}`} className="text-xs">Your response</Label>
              <Textarea id={`r-${task.id}`} value={response} onChange={(e) => setResponse(e.target.value)} rows={3} />
            </div>
          )}
          {task.taskType === "upload" && (
            <div>
              <Label htmlFor={`f-${task.id}`} className="text-xs">Upload file</Label>
              <Input
                id={`f-${task.id}`}
                type="file"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setBusy(true);
                  try { await onUpload(task.id, f); } finally { setBusy(false); }
                }}
              />
            </div>
          )}
          {task.taskType !== "upload" && (
            <Button
              size="sm"
              disabled={busy}
              onClick={async () => { setBusy(true); try { await onComplete(task.id, response || undefined); } finally { setBusy(false); } }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark complete"}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
